import React, { FC, useCallback, useMemo } from 'react';

import { RadioButton } from '@rbilabs/components-library/build/components/radio-button';

import { IPickerAspect } from '@rbi-ctg/menu';
import { IconFallbackImage } from 'components/icons/fallback-image';
import { ListItemGroup } from 'components/list-item-group';
import { ProductListItem } from 'components/product-list-item';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useResetConfirmDialog } from 'hooks/menu/use-reset-confirm-dialog';
import { useFormatCalories } from 'hooks/use-format-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { useProductWizardContext } from 'state/product-wizard';
import { UserSelectionPickerAspect } from 'state/product-wizard/types';
import {
  PickerAspectCalorieMap,
  buildCalorieMapForPickerPossibilities,
  formattedCalorieMapForPickerAspectCalorieMap,
  pickerAspectOptHasResolution,
  reducePickerCalorieMapToRanges,
} from 'utils/menu';

import { PickerGroupContainer } from './picker-aspect-group.styled';
import { IPickerAspectGroupProps, IPickerAspectSelectionArgs } from './types';

interface IPickerAspectGroup extends IPickerAspect {
  showImages: boolean;
}

const PickerAspectGroup: FC<IPickerAspectGroupProps> = ({ pickerData }) => {
  const { formatCalorieRange } = useMenuCalories();
  const { userSelections, updatePickerAspectSelections, isDirty } = useProductWizardContext();
  const formatCalories = useFormatCalories();

  const displayNutritionWithModifiersFromSanity = useFlag(
    LaunchDarklyFlag.DISPLAY_NUTRITION_WITH_MODIFIERS_FROM_SANITY
  );

  const getPreviousPickerSelectionIdentifiers = useCallback(
    (startPoint: number, endPoint: number) =>
      pickerData.pickerAspects
        .slice(startPoint, endPoint)
        .map(aspect => userSelections?.pickerAspects?.[aspect._id]),
    [pickerData.pickerAspects, userSelections]
  );

  const filteredPickerAspects = useMemo<IPickerAspectGroup[]>(
    () =>
      (pickerData.pickerAspects || []).map((currentAspect, index) => {
        const resolvedOpts = currentAspect.pickerAspectOptions.filter(option =>
          pickerAspectOptHasResolution(
            option.identifier,
            getPreviousPickerSelectionIdentifiers(0, index),
            pickerData.options
          )
        );
        return {
          ...currentAspect,
          pickerAspectOptions: resolvedOpts,
          showImages: resolvedOpts.some(option => !!option.image),
        };
      }),
    [pickerData.pickerAspects, pickerData.options, getPreviousPickerSelectionIdentifiers]
  );

  const calorieMap = useMemo(
    () =>
      buildCalorieMapForPickerPossibilities(pickerData, displayNutritionWithModifiersFromSanity),
    [pickerData, displayNutritionWithModifiersFromSanity]
  );

  const getCalorieMapForCurrentPickerAspect = (index: number) => {
    const calorieMapForPickerAspect = Object.values(userSelections.pickerAspects || {})
      .slice(0, index)
      .reduce((map, identifier) => (map ? map[identifier] : null), calorieMap);

    const calorieRangesForPickerAspect = reducePickerCalorieMapToRanges(
      calorieMapForPickerAspect as PickerAspectCalorieMap
    );

    return formattedCalorieMapForPickerAspectCalorieMap(
      calorieRangesForPickerAspect,
      formatCalories
    );
  };

  const handleOnAspectOptionClick = useCallback(
    (args: IPickerAspectSelectionArgs) => {
      const pickerAspectsLength = pickerData.pickerAspects.length;
      // Check new resolved options for the preceding aspects unless it's the last one
      if (pickerAspectsLength > 1 && args.index !== pickerAspectsLength - 1) {
        const pickerSelections = {} as UserSelectionPickerAspect;
        pickerData.pickerAspects.slice(args.index + 1, pickerAspectsLength).forEach(aspect => {
          const possibleOptsForPickerAspect = aspect.pickerAspectOptions.filter(option =>
            pickerAspectOptHasResolution(
              option.identifier,
              [...getPreviousPickerSelectionIdentifiers(0, args.index), args.option.identifier],
              pickerData.options
            )
          );
          const currentMatchingSelectedOption = possibleOptsForPickerAspect.find(
            opt => opt?.identifier === userSelections?.pickerAspects?.[aspect._id]
          );
          // Use the value in state if it's already a possible resolved option
          // Else, choose the first option in the list to default select
          pickerSelections[aspect._id] = currentMatchingSelectedOption
            ? currentMatchingSelectedOption.identifier
            : possibleOptsForPickerAspect[0].identifier;
        });
        updatePickerAspectSelections({
          ...userSelections.pickerAspects,
          ...pickerSelections,
          [args.aspect._id]: args.option.identifier,
        });
      } else {
        updatePickerAspectSelections({
          ...userSelections.pickerAspects,
          [args.aspect._id]: args.option.identifier,
        });
      }
    },
    [
      getPreviousPickerSelectionIdentifiers,
      pickerData.options,
      pickerData.pickerAspects,
      updatePickerAspectSelections,
      userSelections,
    ]
  );

  const { openConfirmationDialog, ResetConfirmDialog } = useResetConfirmDialog(
    handleOnAspectOptionClick,
    'Confirmation: picker aspect will reset customizations to default'
  );

  const mParticle = useMParticleContext();
  const handlePickerAspectChange = useCallback(
    (args: IPickerAspectSelectionArgs) => {
      if (isDirty) {
        openConfirmationDialog(args);
      } else {
        handleOnAspectOptionClick(args);
      }
      mParticle.logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Navigation,
        attributes: {
          component: ClickEventComponentNames.PRODUCT_SELECTION_MODIFICATION,
          text: args.option.identifier,
          headerText: args.aspect.name.locale,
          componentId: args.aspect._id,
        },
      });
    },
    [handleOnAspectOptionClick, isDirty, mParticle, openConfirmationDialog]
  );

  return (
    <PickerGroupContainer data-testid="pickers">
      {filteredPickerAspects.map((aspect, index) => (
        <ListItemGroup
          key={aspect._id}
          header={aspect.name.locale}
          items={aspect.pickerAspectOptions.map(option => (
            <ProductListItem
              tabIndex={-1}
              key={option.identifier}
              controls={
                <RadioButton
                  data-testid={`radio-button-${option.identifier}`}
                  id={`radio-${option.name?.locale?.replace(/\s+/g, '')}`}
                  name={`name-${aspect._id}`}
                  label=""
                  checked={userSelections?.pickerAspects?.[aspect._id] === option.identifier}
                />
              }
              isClickable
              onProductClick={() => handlePickerAspectChange({ aspect, option, index })}
              productProps={{
                name: option.name?.locale,
                description: option.description?.locale,
                image: option.image ? JSON.stringify(option.image) : undefined,
                fallbackImage: aspect.showImages ? IconFallbackImage : undefined,
                calories: formatCalorieRange(
                  getCalorieMapForCurrentPickerAspect(index)?.[option.identifier]
                ),
              }}
            />
          ))}
        />
      ))}
      {ResetConfirmDialog}
    </PickerGroupContainer>
  );
};

export default PickerAspectGroup;
